// FundTip — the hover-tooltip body for one holding, rendered inside the reusable
// ChartTooltip shell by the Treemap / BarChart renderTip prop. Source of truth:
// portfolio3.jsx HoverTip rows. Pure-presentational.
//
// Since FM-2110 the allocation charts plot the TOP LEVEL of the holdings list, so a
// hovered item can be a managed MODEL, or — when a model bar/tile is segmented — a
// fund HELD INSIDE one (`_parent` set by HoldingsViz). The three shapes state
// different rows, exactly as the design's HoverTip does:
//   • fund inside a model — "inside <model>" meta, its weight IN the model, and
//     "Set by · Plan advisor" (the participant elects the model; the advisor sets
//     the mix), footed "Held through <model> →";
//   • a model — its underlying-fund count, no shares/NAV/expense of its own;
//   • a standalone fund — its own Future election, shares · NAV and expense.
// A model has no shares, no NAV, no single expense ratio and no fact sheet — those
// rows are omitted rather than rendered as "—", and every numeric access is guarded,
// because a missing figure must never take the page down with it.
//
// The footer is a plain NOTE, not a control: "Click on bar to view fund fact sheet"
// (or "tile" on the treemap — see `surface`). It points at the shape under the
// pointer, which is where the click belongs — the card follows the cursor and stays
// inert, so it never intercepts the click it is describing.
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { cx } from '../../../../utils/cx';
import { formatCurrency, formatPercent } from '../../../../utils/format';
import { electionText, shareText } from '../portfolio.adapters';
import { InfoIcon } from '../../../icons/Icons';
import { ModelTag } from './ModelRow';
import './fund-tip.css';

// How many member funds the model tip names before "+N more".
const TOP_FUNDS = 3;

export function FundTip({ fund, plan, surface = 'bar' }) {
  const { t } = useTranslation();
  if (!fund) return null;

  const isModel = fund.type === 'model';
  const parent = fund._parent || null;
  const isChild = !!parent || fund.isModelChild === true;
  const pct = fund.balancePct != null ? fund.balancePct : fund.pct;
  const holds = (fund.children || []).length;

  const shares =
    fund.shares == null
      ? null
      : fund.shares.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

  // The note names the shape the participant is pointing at, not a generic "chart
  // element" — one key per surface rather than an interpolated noun, because
  // "la barra"/"el bloque" carry their own articles in the translated packs.
  const hintKey = surface === 'tile' ? 'portfolio.fundTip.clickTile' : 'portfolio.fundTip.clickBar';
  // A model's note says what ITS click opens (the funds inside), in the same voice.
  const modelHintKey = surface === 'tile' ? 'portfolio.fundTip.clickTileModel' : 'portfolio.fundTip.clickBarModel';

  // A model's meta line is its ticker (if any) and the Model tag — "Managed model" as a
  // class name said less than the tag does.
  const meta = (isModel ? [fund.ticker] : [fund.ticker, fund.assetClass]).filter(Boolean).join(' · ');
  const insideMeta =
    parent && (parent.ticker || parent.name)
      ? ` · ${t('portfolio.fundTip.inside', { name: parent.ticker || parent.name })}`
      : '';
  // The biggest members by weight, so two models can be told apart before a click.
  const topFunds = isModel
    ? [...(fund.children || [])]
        .sort((a, b) => (b.weightInModel || 0) - (a.weightInModel || 0))
        .slice(0, TOP_FUNDS)
    : [];

  return (
    <div className="fund-tip">
      <div className="fund-tip__h">
        <span className="fund-tip__sw" style={{ background: fund.color }} />
        <div>
          <div className="fund-tip__name">{fund.name}</div>
          <div className="fund-tip__meta">
            {meta}
            {insideMeta}
            {isModel && <ModelTag t={t} />}
          </div>
        </div>
      </div>
      <div className="fund-tip__rows">
        {isChild && fund.weightInModel != null && (
          <div>
            <span>{t('portfolio.fundTip.weightInModel')}</span>
            <strong>{fund.weightLabel ?? `${fund.weightInModel}%`}</strong>
          </div>
        )}
        <div>
          <span>{t('portfolio.fundTip.balance')}</span>
          <strong>{formatCurrency(fund.balance)}</strong>
        </div>
        <div>
          <span>{t('portfolio.fundTip.allocation')}</span>
          <strong>{pct == null ? '—' : shareText(fund)}</strong>
        </div>
        {/* A fund inside a model has no election of its own — the participant elects
            the model and the advisor sets the mix (design HoverTip's "Set by" row).
            Everything else states its Future election through the same rule the
            By-Fund column uses, so "n/a" plans read "n/a" here too. */}
        {isChild ? (
          <div>
            <span>{t('portfolio.fundTip.setBy')}</span>
            <strong>{t('portfolio.fundTip.planAdvisor')}</strong>
          </div>
        ) : (
          <div>
            <span>{t('portfolio.fundTip.futureElection')}</span>
            <strong>{electionText(fund, plan)}</strong>
          </div>
        )}
        {isModel && holds > 0 && (
          <>
            <div>
              <span>{t('portfolio.fundTip.fundsInside')}</span>
              <strong>{holds}</strong>
            </div>
            <div className="fund-tip__top">
              {/* Whole percents: three "TICKER 54.5%" entries overran the 230px card. */}
              {topFunds.map((c) => `${c.ticker} ${c.weightLabel ?? (c.weightInModel == null ? '—' : `${Math.round(c.weightInModel)}%`)}`).join(' · ')}
              {holds > TOP_FUNDS && ` · ${t('portfolio.fundTip.more', { count: holds - TOP_FUNDS })}`}
            </div>
          </>
        )}
        {shares !== null && (
          <div>
            <span>{t('portfolio.fundTip.sharesNav')}</span>
            <strong>
              {shares} · {formatCurrency(fund.nav)}
            </strong>
          </div>
        )}
        {fund.expense != null && (
          <div>
            <span>{t('portfolio.fundTip.expenseRatio')}</span>
            <strong>{fund.expense.toFixed(2)}%</strong>
          </div>
        )}
        {fund.ytd != null && (
          <div>
            <span>{t('portfolio.fundTip.ytd')}</span>
            <strong className={cx(fund.ytd >= 0 ? 'fund-tip__pos' : 'fund-tip__neg')}>
              {formatPercent(fund.ytd, { digits: 2, signed: true })}
            </strong>
          </div>
        )}
      </div>
      {/* A standalone fund's footer is a NOTE saying where its fact sheet is. It
          replaces "Click for fact sheet →", which described a click the card could
          not receive: .chart-tip is inert and pointer-following, so nothing in here
          is reachable — and it should not be. The bar (or tile) IS the target, so the
          note names it and the click goes to the shape, not to the tooltip.
          It reads in sentence case at note size rather than the footer's uppercase
          label type: a whole instruction set in 10px caps needs two lines and reads
          as shouting. Quiet grey, not the footer's link blue — nothing here is
          clickable and blue would promise otherwise.
          It names the shape the participant is actually looking at, which is why
          `surface` exists: the same card serves the bar chart and the treemap, and
          "click on bar" over a tile would point at nothing on screen.
          A model has no fact sheet — its footer says the click opens its member funds
          (the model allocation modal); a fund inside one names the model it is held
          through. */}
      <div className="fund-tip__foot">
        {isChild && parent?.name ? (
          t('portfolio.fundTip.heldThrough', { name: parent.name })
        ) : (
          <span className="fund-tip__note">
            <InfoIcon className="fund-tip__note-ic" width={12} height={12} aria-hidden="true" />
            <span>{t(isModel ? modelHintKey : hintKey)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

FundTip.propTypes = {
  fund: PropTypes.shape({
    /** 'model' switches to the model treatment; anything else reads as a fund. */
    type: PropTypes.string,
    ticker: PropTypes.string,
    name: PropTypes.string,
    assetClass: PropTypes.string,
    color: PropTypes.string,
    balance: PropTypes.number,
    pct: PropTypes.number,
    balancePct: PropTypes.number,
    shares: PropTypes.number,
    nav: PropTypes.number,
    expense: PropTypes.number,
    ytd: PropTypes.number,
    children: PropTypes.array,
    /** Set on funds held inside a model (adapter) — switches to the child rows. */
    isModelChild: PropTypes.bool,
    /** Share of the model this fund makes up, in points (adapter). */
    weightInModel: PropTypes.number,
    /** The model this fund is held through (attached by HoldingsViz). */
    _parent: PropTypes.object,
  }),
  /** Plan record — the Future-election row asks it whether elections apply. */
  plan: PropTypes.object,
  /**
   * Which chart is under the pointer, so the footer note names the right shape:
   * 'bar' → "Click on bar …", 'tile' → "Click on tile …" (the treemap). Only reaches
   * the note; every other row is identical on both surfaces.
   */
  surface: PropTypes.oneOf(['bar', 'tile']),
};

export default FundTip;
